/* eslint-disable react-hooks/exhaustive-deps */
import {
  EditOutlined,
  PlusOutlined,
  DeleteOutlined,
  GiftOutlined,
  UploadOutlined,
  HistoryOutlined,
} from "@ant-design/icons";
import { Table, Button, message, Modal, Input, InputNumber, Form, Upload, Space, Tooltip } from "antd";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { apiClient, getStorageUrl } from "../../../shared/api/AllApi";
import { invalidateCatalog, useProductsCatalog } from "../../../hooks/useCatalogData";
import { parseLocalizedName } from "../../../utils/localization";
import usePermissions from "../../../hooks/usePermissions";
import { ListPage, StatCard, T } from "../../../shared/ui/PageShell";
import useTablePagination from "../../../shared/ui/useTablePagination";

const GetProducts = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const [SearchText, setSearchText] = useState("");
  const [dataSource, setDataSource] = useState([]);
  const [deleteItemId, setDeleteItemId] = useState(null);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [saving, setSaving] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [form] = Form.useForm();
  const { data: productsData, loading, refresh } = useProductsCatalog();
  const { t, i18n } = useTranslation();
  const { pagination } = useTablePagination();
  const canAdd = hasPermission("products.add");
  const canEdit = hasPermission("products.edit");
  const canManageImages = hasPermission("products.images");

  useEffect(() => {
    setDataSource(
      productsData.map((item) => ({
        id: item.id,
        nameKurdish: parseLocalizedName(item.name, i18n.language, "N/A"),
        image: item.image_url || null,
        sku: item.sku || "",
        sortOrder: Number(item.sort_order) || 0,
      }))
    );
  }, [productsData, i18n.language]);

  useEffect(() => () => {
    if (imagePreview) URL.revokeObjectURL(imagePreview);
  }, [imagePreview]);

  const resetProductForm = () => {
    if (imagePreview) URL.revokeObjectURL(imagePreview);
    setImagePreview(null);
    setImageFile(null);
    setEditingProduct(null);
    form.resetFields();
  };

  const closeProductForm = () => {
    setFormOpen(false);
    resetProductForm();
  };

  const openAdd = () => {
    if (!canAdd) return;
    resetProductForm();
    setFormOpen(true);
  };

  const openEdit = (record) => {
    if (!canEdit) return;
    resetProductForm();
    setEditingProduct(record);
    form.setFieldsValue({ name: record.nameKurdish, sortOrder: record.sortOrder });
    setFormOpen(true);
  };

  const saveSortOrder = async (record, value) => {
    const nextValue = Number.isFinite(value) ? value : 0;
    if (nextValue === record.sortOrder) return;
    setDataSource((prev) =>
      prev.map((p) => (p.id === record.id ? { ...p, sortOrder: nextValue } : p))
    );
    try {
      await apiClient.put(`/products/${record.id}`, { sort_order: nextValue });
      invalidateCatalog("products");
      await refresh();
    } catch (error) {
      message.error(
        error?.response?.data?.message || t("productUpdateFailed", "Failed to update product")
      );
      await refresh();
    }
  };

  const selectImage = (file) => {
    const allowedTypes = ["image/jpeg", "image/png", "image/webp"];
    const extension = file.name?.toLowerCase().match(/\.[^.]+$/)?.[0];
    const allowedExtensions = [".jpg", ".jpeg", ".png", ".webp"];
    if (
      !allowedTypes.includes(file.type)
      || !allowedExtensions.includes(extension)
      || file.size > 4 * 1024 * 1024
    ) {
      message.error(t("productImageInvalid", "Use a JPG, PNG, or WebP image up to 4 MB."));
      return Upload.LIST_IGNORE;
    }
    if (imagePreview) URL.revokeObjectURL(imagePreview);
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
    return false;
  };

  const saveProduct = async () => {
    if (editingProduct ? !canEdit : !canAdd) return;
    try {
      const values = await form.validateFields();
      setSaving(true);
      const name = String(values.name || "").trim();
      const payload = {
        name: JSON.stringify({ en: name, ar: name, ku: name }),
        sort_order: Number.isFinite(values.sortOrder) ? values.sortOrder : 0,
      };

      // Omit image_url unless a permitted, validated replacement was selected.
      // On edit this preserves the existing image instead of sending null/empty.
      if (canManageImages && imageFile) {
        const formData = new FormData();
        formData.append("file", imageFile);
        const upload = await apiClient.post("/products/image", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        if (upload.data?.path) payload.image_url = upload.data.path;
      }

      if (editingProduct) {
        await apiClient.put(`/products/${editingProduct.id}`, payload);
        message.success(t("productUpdated", "Product updated successfully"));
      } else {
        await apiClient.post("/products", payload);
        message.success(t("successMessage", "Form submitted successfully"));
      }
      invalidateCatalog("products");
      await refresh();
      closeProductForm();
    } catch (error) {
      if (error?.errorFields) return;
      message.error(
        error?.response?.data?.message
        || t(editingProduct ? "productUpdateFailed" : "errorMessage", "Failed to submit form. Please try again.")
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (id) => { setDeleteItemId(id); setIsDeleteModalVisible(true); };
  const handleConfirmedDelete = async () => {
    try {
      await apiClient.delete(`/products/${deleteItemId}`);
      message.success(t("productDeleted", "Product deleted successfully"));
      await refresh();
      setDeleteItemId(null);
      setIsDeleteModalVisible(false);
    } catch {
      message.error(t("productDeleteFailed", "Failed to delete product"));
    }
  };

  const columns = [
    {
      title: t("image", "Image"),
      dataIndex: "image",
      key: "image",
      width: 72,
      render: (image) => image ? (
        <img
          src={getStorageUrl(image)}
          alt={t("productImageAlt", "Product")}
          style={{ width: 48, height: 48, objectFit: "cover", borderRadius: 10, border: `1px solid ${T.border}` }}
        />
      ) : (
        <div style={{
          width: 48,
          height: 48,
          borderRadius: 10,
          background: T.borderL,
          border: `1px solid ${T.border}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: T.muted,
        }}>
          <GiftOutlined />
        </div>
      ),
    },
    {
      title: t("name", "Name"),
      dataIndex: "nameKurdish",
      key: "nameKurdish",
      render: (text, record) => (
        <div className="catalog-product-cell">
          <Link to={`/products/${record.id}/history`} className="catalog-product-cell__name">
            {text}
          </Link>
          {record.sku ? (
            <span className="catalog-product-cell__meta">SKU {record.sku}</span>
          ) : null}
        </div>
      ),
      filteredValue: [SearchText],
      onFilter: (value, record) =>
        String(record.nameKurdish).toLowerCase().includes(value.toLowerCase()) ||
        String(record.sku || "").toLowerCase().includes(value.toLowerCase()) ||
        String(record.id).toLowerCase().includes(value.toLowerCase()),
    },
    {
      title: t("productSortOrder", "Display order in POS"),
      dataIndex: "sortOrder",
      key: "sortOrder",
      width: 150,
      sorter: (a, b) => a.sortOrder - b.sortOrder,
      render: (value, record) => (
        <InputNumber
          key={`${record.id}-${value}`}
          size="small"
          min={0}
          step={1}
          defaultValue={value}
          disabled={!canEdit}
          style={{ width: 90 }}
          onBlur={(e) => saveSortOrder(record, Number(e.target.value))}
          onPressEnter={(e) => saveSortOrder(record, Number(e.target.value))}
        />
      ),
    },
    {
      title: t("actions", "Actions"),
      key: "actions",
      width: 168,
      fixed: "right",
      render: (_, record) => (
        <Space size={6}>
          <Tooltip title={t("productHistory", "History")}>
            <Button
              size="small"
              icon={<HistoryOutlined />}
              onClick={() => navigate(`/products/${record.id}/history`)}
              style={{ borderRadius: 8 }}
            />
          </Tooltip>
          {canEdit && (
            <Tooltip title={t("edit", "Edit")}>
              <Button
                type="primary"
                size="small"
                icon={<EditOutlined />}
                onClick={() => openEdit(record)}
                style={{ borderRadius: 8 }}
              />
            </Tooltip>
          )}
          {hasPermission("products.delete") && (
            <Tooltip title={t("delete", "Delete")}>
              <Button
                danger
                size="small"
                icon={<DeleteOutlined />}
                onClick={() => handleDelete(record.id)}
                style={{ borderRadius: 8 }}
              />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  const stats = [
    <StatCard
      key="total"
      icon={<GiftOutlined style={{ color: T.primary }} />}
      label={t("products", "Products")}
      value={dataSource.length}
      iconBg="#dbeafe"
    />,
  ];

  const toolbar = (
    <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
      <Input.Search
        allowClear
        placeholder={t("searchProductsPlaceholder", "Search by name or SKU...")}
        style={{ width: 300, maxWidth: "100%" }}
        onSearch={(v) => setSearchText(v)}
        onChange={(e) => setSearchText(e.target.value)}
      />
      <div style={{ flex: 1 }} />
      {canAdd && (
        <Button type="primary" icon={<PlusOutlined />} onClick={openAdd}>
          {t("addItem", "Add Item")}
        </Button>
      )}
    </div>
  );

  return (
    <>
      <ListPage
        breadcrumb={[t("sidebarSectionCatalog", "Catalog"), t("products", "Products")]}
        title={t("productList", "Product List")}
        subtitle={t("productListSubtitle", "Catalog products.")}
        stats={stats}
        toolbar={toolbar}
      >
        <Table
          loading={loading}
          rowKey="id"
          dataSource={dataSource}
          columns={columns}
          pagination={{ ...pagination, showTotal: (total) => `${total}` }}
          scroll={{ x: 720 }}
        />
      </ListPage>

      <Modal
        title={editingProduct ? t("updateProduct", "Update Product") : t("addProduct", "Add Product")}
        open={formOpen}
        onCancel={closeProductForm}
        onOk={saveProduct}
        okText={t("purchaseOrdersSave", "Save")}
        confirmLoading={saving}
        width={560}
        destroyOnHidden
      >
        <Form form={form} layout="vertical" requiredMark>
          <Form.Item
            name="name"
            label={t("name", "Name")}
            rules={[{ required: true, whitespace: true, message: t("enterProductName", "Enter product name") }]}
          >
            <Input maxLength={255} placeholder={t("enterProductName", "Enter product name")} />
          </Form.Item>

          <Form.Item
            name="sortOrder"
            label={t("productSortOrder", "Display order in POS")}
            tooltip={t("productSortOrderHint", "Lower numbers show first in the POS product grid. Products with the same number are ordered by name.")}
            initialValue={0}
          >
            <InputNumber style={{ width: "100%" }} min={0} step={1} placeholder="0" />
          </Form.Item>

          {canManageImages && (
            <Form.Item label={t("image", "Image")}>
              <Upload
                accept=".jpg,.jpeg,.png,.webp"
                showUploadList={false}
                beforeUpload={selectImage}
                maxCount={1}
              >
                {imagePreview || editingProduct?.image ? (
                  <img
                    src={imagePreview || getStorageUrl(editingProduct.image)}
                    alt={t("productImageAlt", "Product")}
                    style={{ width: 150, height: 150, objectFit: "cover", borderRadius: 12, border: `2px dashed ${T.border}`, cursor: "pointer" }}
                  />
                ) : (
                  <div style={{ width: 150, height: 150, borderRadius: 12, border: `2px dashed ${T.border}`, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", cursor: "pointer", gap: 8, color: T.muted }}>
                    <UploadOutlined style={{ fontSize: 28 }} />
                    <span style={{ fontSize: 12 }}>{t("uploadImage", "Upload image")}</span>
                  </div>
                )}
              </Upload>
              {editingProduct?.image && !imageFile && (
                <div style={{ marginTop: 6, fontSize: 12, color: T.muted }}>
                  {t("productImagePreservedHint", "The current image is kept unless you select a new one.")}
                </div>
              )}
            </Form.Item>
          )}
        </Form>
      </Modal>

      <Modal
        title={t("confirmDelete", "Confirm Delete")}
        open={isDeleteModalVisible}
        onOk={handleConfirmedDelete}
        onCancel={() => setIsDeleteModalVisible(false)}
        okText={t("confirm", "Confirm")}
        cancelText={t("cancel", "Cancel")}
        centered
      >
        <p>{t("confirmDeleteMessage", "Are you sure you want to delete this product?")}</p>
      </Modal>
    </>
  );
};

export default GetProducts;
